import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Box,
  IconButton,
  Button,
  Stack,
  Card,
  Collapse,
  Link,
  useTheme,
} from "@mui/material";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import Tooltip from "@mui/material/Tooltip";
import PreviewIcon from '@mui/icons-material/Preview';
import { PopoverComponent, makeLabel, makeLabelRow } from "./Extras";
import { CustomItemPreviewView } from "../CustomItemEditView";
import { POPreviewView } from "./POPreviewView";
import { isMobile } from 'react-device-detect';
import { PurchaseOrderContentField } from "./ContentField";
import Loading from "../../components/Loading";
import { PurchaseOrderTableRow } from "./PurchaseOrderTableRow";
import { useUpdatePurchaseOrderDataActionMutation } from "../../features/po/poSlice";
import { useDebounce } from "../../helpers/debounce";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { UploadCSVBox } from "./UploadCSVBox";
import GetTheme from "../../helpers/theme";
import { CSVToDicts } from "../../helpers/csv";

const FormColors = {
  MainBg:   "#f0f0f7",
  GroupBg:   "#f7f7f7",
  PriceBg:  "#e0e0f7",
  RowBg:    "#fefefe",
  RowBgAlt: "#fafafa"
};

const iconLabelStyle = {
  flexDirection: 'column',
  fontSize: '10px',
};

const TableHeader = ({ident, label, tableOpenState, setTableOpenState, isAdmin=false, addRows=null}) => {
    const [ showUpload, setShowUpload ] = useState(false);
    const theme = GetTheme();
    return (
      <Box display="flex" alignItems="center">
        <IconButton
          onClick={() => {
            setTableOpenState(!tableOpenState)
          }}
        >
          {tableOpenState ? (
            <ExpandLessIcon />
          ) : (
            <ExpandMoreIcon />
          )}
        </IconButton>
        <Typography variant="h2" onClick={() => {setTableOpenState(!tableOpenState)}}>{label}</Typography>
        {
          isAdmin && <>
          <Box style={{flexGrow: 1}} />
          <IconButton color={'primary'} style={iconLabelStyle} onClick={(e) => {
            e.stopPropagation();
          }} component={Link} variant="outlined" target="_blank" href={`${process.env.REACT_APP_API_URL}v1/po/csv/blank/${ident}`}>
            <InsertDriveFileOutlinedIcon />
            <div>Blank CSV</div>
          </IconButton>
          <UploadCSVBox onParse={(data) => {
            const dataRows = CSVToDicts(data);
            if (addRows) {
              addRows(dataRows);
            }
          }}>
            <Stack direction="column" style={{alignItems: 'center', padding: 0}}>
            <InsertDriveFileIcon color={'primary'} />
            <div style={{...iconLabelStyle, color: theme.palette.primary.main}}>Import CSV</div>
            </Stack>
          </UploadCSVBox>
          </>
        }
      </Box>
    );
  };


const PurchaseOrderTableView = ({table, order, handleRowChange, formData, formTotalsData, isAdmin=false, readOnly, validationErrors: parentValidationErrors, handleTableChange: parentHandleTableChange}) => {
    const [tableOpenState, setTableOpenState] = useState(null);
    const [inlinePreview, setInlinePreview] = useState(-1);
    const [validationErrors, setValidationErrors] = useState(parentValidationErrors?.[table.key] || {});
    const minElementWidth = isMobile ? 160 : 200;
    const [page, setPage] = useState(0);
    
    useEffect(() => {
      if(tableOpenState === null && (order?.[table.key] || []).length > 0) {
        if (order?.[table.key] || [].length > 0) {
          setTableOpenState(true);
        } else {
          setTableOpenState(false);
        }
      }
    }, [order, table, tableOpenState])

    var table_rows = order?.[table.key] || [];

    const handleTableChange = ({rowKey, fieldKey, value, applyToAll=false}) => {
      parentHandleTableChange({tableKey: table.key, rowKey: rowKey, fieldKey: fieldKey, value: value, applyToAll: applyToAll});
      setValidationErrors((prev) => ({...prev,
          [rowKey]: prev?.[rowKey]
            ? prev?.[rowKey].filter(
                (key) => key !== fieldKey
            )
            : [],
      }));
    }

    if(!formData || !order) {
      return <Loading />;
    }

    const per_page = 20;
    const page_begin = page * per_page;
    const page_end = page * per_page + per_page - 1;
    const page_count = Math.ceil(table_rows.length / per_page);

    function moveToLastPage() {
      const new_page_count = Math.ceil((table_rows.length + 1) / per_page);
      if(page < new_page_count - 1) {
        setPage(Math.max(new_page_count - 1, 0));
      }
    }

    const tableRowButtons = (row_key, floatingPreview=true, style="small") => {
      const clickPreview = () => {
        setInlinePreview(inlinePreview === row_key ? -1 : row_key);
      };
      return <>
        {custom ? <></> :
        <Tooltip title="Add new row">
          <AddIcon
            onClick={() => {handleRowChange({table_key: table.key, change: "add_row", row_key: row_key});setInlinePreview(-1);moveToLastPage();}}
          />
        </Tooltip>
        }
        <Tooltip title="Duplicate row">
          <ContentCopyIcon
            onClick={() => {handleRowChange({table_key: table.key, change: "duplicate_row", row_key: row_key});setInlinePreview(-1);moveToLastPage();}}
            />
        </Tooltip>
        <Tooltip title="Delete row">
          <DeleteIcon
            onClick={() => {handleRowChange({table_key: table.key, change: "delete_row", row_key: row_key, custom: custom});setInlinePreview(-1);}}
          />
        </Tooltip>
        <Tooltip title="Preview">
          {style==="large" ?
          <Button sx={{alignSelf: "center"}} variant="outlined" onClick={clickPreview}>Preview</Button>
          :
          <PreviewIcon
            onClick={clickPreview}
          />}
        </Tooltip>
      </>
    }

    function applyToAll(field, row_key=0) {
      // get the value in question
      let value = table_rows[row_key][field.key];
      // set the order data replacing that table
      handleTableChange({
        rowKey: row_key,
        fieldKey: field.key,
        value: value,
        applyToAll: true});
    }
    
    const custom = table.key.startsWith("custom_");
    const custom_header = custom ? <TableCell sx={{verticalAlign: "bottom", padding: "4px", margin: 0}} key="header_code">Custom Item</TableCell> : <></>;
    let conditions = formData.definitions._conditions;
    let all_fields = table.fields.filter((f) => (!f.custom || f.type === "auto"));

    // Are we seeing anything?
    if(page >= page_count && page > 0) {
      setPage(page_count - 1);
    }

    const page_buttons = page_count > 1 ? <Stack direction="row" sx={{marginBottom: 2}}>
      {[...Array(page_count)].map((i, pn) => <Button
          variant={page === pn ? "contained" : "outlined"}
          onClick={() => {setPage(pn)}}
        >
        {`${pn*per_page+1}-${Math.min(pn*per_page+per_page, table_rows.length+1)}`}
      </Button>)}
      </Stack> : <></>;

    return <div key={table.label}>
      <Paper variant="outlined" sx={{ paddingLeft: isMobile ? 0 : 2, paddingRight: isMobile ? 0 : 2, backgroundColor: "#f7f7f7" }}>
        <TableHeader
          ident={table.key}
          label={table.label}
          tableOpenState={tableOpenState}
          setTableOpenState={setTableOpenState}
          isAdmin={isAdmin}
          addRows={(rows) => {
            handleRowChange({table_key: table.key, change: "add_rows", row_key: null, data: rows});
            setTableOpenState(true);
            setInlinePreview(-1);
            moveToLastPage();
          }}
        />
        {tableOpenState && <>
        {custom_header}
        {page_buttons}
        {table_rows.map((row_data, row_key) => {
          if(row_key >= page_begin && row_key <= page_end)
            return <PurchaseOrderTableRow
              fields={all_fields}
              conditions={conditions}
              definitions={formData.definitions}
              data={row_data}
              key={row_key}
              index={row_key}
              minElementWidth={minElementWidth}
              inlinePreview={inlinePreview}
              readOnly={readOnly}
              total_rows={table_rows.length}
              handleTableChange={handleTableChange}
              applyToAll={applyToAll}
              validationErrors={validationErrors?.[row_key] || {}}
              buttons={tableRowButtons(row_key, false)}
            />;
          return <></>;
          })
        }
        {page_buttons}
        <Button
            sx={{marginBottom: 2}}
            variant="outlined"
            onClick={() => { handleRowChange({table_key: table.key, change: "add_row", row_key: null});setInlinePreview(-1);moveToLastPage();}}
          ><AddIcon /> Add a line item</Button>
          </>}
      </Paper>
    </div>
};

export {PurchaseOrderTableView};